/**
 * Class Calendar Component
 *
 * A monthly calendar view for displaying class sessions.
 * Shows class sessions on their scheduled dates with visual indicators
 * for status and allows clicking to view details.
 */

import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useState } from "react"
import {
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiMapPin,
  FiX,
} from "react-icons/fi"

import type { ClassSessionResponse } from "@/types/class_session"

// Days of the week
const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

// Month names
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

// Status colors
const statusColors: Record<string, string> = {
  scheduled: "blue",
  in_progress: "green",
  completed: "gray",
  cancelled: "red",
  rescheduled: "orange",
}

// Format time from ISO or time string
function formatTime(timeStr: string | null): string {
  if (!timeStr) return ""

  // Handle ISO datetime string
  if (timeStr.includes("T")) {
    const date = new Date(timeStr)
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  }

  // Handle HH:MM:SS format
  const [hours, minutes] = timeStr.split(":")
  const hour = Number.parseInt(hours, 10)
  const ampm = hour >= 12 ? "PM" : "AM"
  const hour12 = hour % 12 || 12
  return `${hour12}:${minutes} ${ampm}`
}

// Get days in a month
function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

// Get the day of week the month starts on (0 = Sunday)
function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay()
}

// Check if two dates are the same day
function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

// Check if a date is today
function isToday(date: Date): boolean {
  return isSameDay(date, new Date())
}

// Enriched class session with display info
export interface EnrichedClassSession extends ClassSessionResponse {
  class_name?: string
  swim_level?: string
  location?: string
  instructor_name?: string
}

export interface ClassCalendarProps {
  sessions: EnrichedClassSession[]
  onSessionClick?: (session: EnrichedClassSession) => void
}

// Calendar day cell component
function CalendarDay({
  date,
  sessions,
  isCurrentMonth,
  onSessionClick,
}: {
  date: Date
  sessions: EnrichedClassSession[]
  isCurrentMonth: boolean
  onSessionClick?: (session: EnrichedClassSession) => void
}) {
  const dayOfMonth = date.getDate()
  const today = isToday(date)
  const maxVisible = 2

  return (
    <Box
      minH="100px"
      p={1}
      bg={today ? "ui.backgroundAlt" : isCurrentMonth ? "white" : "gray.50"}
      borderWidth="1px"
      borderColor={today ? "ui.main" : "gray.100"}
      borderRadius="md"
      opacity={isCurrentMonth ? 1 : 0.5}
    >
      <Text
        fontSize="sm"
        fontWeight={today ? "bold" : "medium"}
        color={today ? "ui.main" : isCurrentMonth ? "gray.700" : "gray.400"}
        mb={1}
      >
        {dayOfMonth}
      </Text>

      <VStack align="stretch" gap={1}>
        {sessions.slice(0, maxVisible).map((session) => (
          <Box
            key={session.id}
            bg={`${statusColors[session.status || "scheduled"]}.100`}
            borderLeftWidth="3px"
            borderLeftColor={`${statusColors[session.status || "scheduled"]}.500`}
            borderRadius="sm"
            px={1}
            py={0.5}
            cursor="pointer"
            onClick={() => onSessionClick?.(session)}
            _hover={{
              bg: `${statusColors[session.status || "scheduled"]}.200`,
            }}
            transition="all 0.1s"
          >
            <Text
              fontSize="xs"
              fontWeight="medium"
              color="gray.800"
              lineClamp={1}
            >
              {session.class_name || "Class"}
            </Text>
            <Text fontSize="xs" color="gray.600">
              {formatTime(session.session_start)}
            </Text>
          </Box>
        ))}

        {sessions.length > maxVisible && (
          <Text fontSize="xs" color="gray.500" textAlign="center">
            +{sessions.length - maxVisible} more
          </Text>
        )}
      </VStack>
    </Box>
  )
}

// Session detail popover/card
function SessionDetail({
  session,
  onClose,
}: {
  session: EnrichedClassSession
  onClose: () => void
}) {
  return (
    <Card.Root
      position="absolute"
      top="50%"
      left="50%"
      transform="translate(-50%, -50%)"
      zIndex={100}
      bg="white"
      boxShadow="xl"
      borderRadius="xl"
      maxW="sm"
      w="full"
    >
      <Card.Body p={5}>
        <VStack align="stretch" gap={4}>
          <Flex justify="space-between" align="start">
            <Box>
              <Text fontWeight="bold" fontSize="lg" color="gray.800">
                {session.class_name || "Class Session"}
              </Text>
              {session.swim_level && (
                <Badge colorPalette="blue" variant="subtle" mt={1}>
                  {session.swim_level}
                </Badge>
              )}
            </Box>
            <Button size="xs" variant="ghost" onClick={onClose}>
              <FiX />
            </Button>
          </Flex>

          <VStack align="stretch" gap={2}>
            <HStack fontSize="sm" color="gray.600">
              <Icon color="gray.400">
                <FiCalendar />
              </Icon>
              <Text>
                {new Date(session.session_date).toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </Text>
            </HStack>

            <HStack fontSize="sm" color="gray.600">
              <Icon color="gray.400">
                <FiClock />
              </Icon>
              <Text>
                {formatTime(session.session_start)} -{" "}
                {formatTime(session.session_end)}
              </Text>
            </HStack>

            {(session.location_override || session.location) && (
              <HStack fontSize="sm" color="gray.600">
                <Icon color="gray.400">
                  <FiMapPin />
                </Icon>
                <Text>{session.location_override || session.location}</Text>
              </HStack>
            )}
          </VStack>

          <Box>
            <Badge
              colorPalette={statusColors[session.status || "scheduled"]}
              variant="subtle"
              px={3}
              py={1}
              borderRadius="full"
            >
              {(session.status || "scheduled").replace("_", " ").toUpperCase()}
            </Badge>
          </Box>

          {session.notes && (
            <Box bg="gray.50" borderRadius="md" p={3}>
              <Text fontSize="sm" color="gray.600">
                {session.notes}
              </Text>
            </Box>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

export function ClassCalendar({
  sessions,
  onSessionClick,
}: ClassCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedSession, setSelectedSession] =
    useState<EnrichedClassSession | null>(null)

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  const daysInMonth = getDaysInMonth(year, month)
  const firstDayOfMonth = getFirstDayOfMonth(year, month)

  // Get previous month's trailing days
  const prevMonthDays = getDaysInMonth(year, month - 1)

  // Build calendar grid (6 weeks × 7 days = 42 cells)
  const calendarDays: { date: Date; isCurrentMonth: boolean }[] = []

  // Previous month's trailing days
  for (let i = firstDayOfMonth - 1; i >= 0; i--) {
    calendarDays.push({
      date: new Date(year, month - 1, prevMonthDays - i),
      isCurrentMonth: false,
    })
  }

  // Current month's days
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push({
      date: new Date(year, month, day),
      isCurrentMonth: true,
    })
  }

  // Next month's leading days (fill to 42 cells)
  const remainingDays = 42 - calendarDays.length
  for (let day = 1; day <= remainingDays; day++) {
    calendarDays.push({
      date: new Date(year, month + 1, day),
      isCurrentMonth: false,
    })
  }

  // Group sessions by date
  const sessionsByDate = sessions.reduce(
    (acc, session) => {
      const dateKey = session.session_date.split("T")[0]
      if (!acc[dateKey]) {
        acc[dateKey] = []
      }
      acc[dateKey].push(session)
      return acc
    },
    {} as Record<string, EnrichedClassSession[]>,
  )

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1))
  }

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1))
  }

  const handleToday = () => {
    setCurrentDate(new Date())
  }

  const handleSessionClick = (session: EnrichedClassSession) => {
    if (onSessionClick) {
      onSessionClick(session)
    } else {
      setSelectedSession(session)
    }
  }

  return (
    <Box position="relative">
      {/* Header */}
      <Flex justify="space-between" align="center" mb={4}>
        <HStack gap={2}>
          <Button size="sm" variant="ghost" onClick={handlePrevMonth}>
            <FiChevronLeft />
          </Button>
          <Heading size="md" minW="200px" textAlign="center">
            {MONTH_NAMES[month]} {year}
          </Heading>
          <Button size="sm" variant="ghost" onClick={handleNextMonth}>
            <FiChevronRight />
          </Button>
        </HStack>

        <Button size="sm" variant="outline" onClick={handleToday}>
          Today
        </Button>
      </Flex>

      {/* Day headers */}
      <SimpleGrid columns={7} gap={1} mb={1}>
        {DAYS_OF_WEEK.map((day) => (
          <Box
            key={day}
            textAlign="center"
            py={2}
            bg="gray.50"
            borderRadius="md"
          >
            <Text fontSize="sm" fontWeight="semibold" color="gray.600">
              {day}
            </Text>
          </Box>
        ))}
      </SimpleGrid>

      {/* Calendar grid */}
      <Grid templateColumns="repeat(7, 1fr)" gap={1}>
        {calendarDays.map(({ date, isCurrentMonth }, index) => {
          const dateKey = date.toISOString().split("T")[0]
          const daySessions = sessionsByDate[dateKey] || []

          return (
            <CalendarDay
              key={index}
              date={date}
              sessions={daySessions}
              isCurrentMonth={isCurrentMonth}
              onSessionClick={handleSessionClick}
            />
          )
        })}
      </Grid>

      {/* Legend */}
      <Flex justify="center" gap={4} mt={4} flexWrap="wrap">
        {Object.entries(statusColors).map(([status, color]) => (
          <HStack key={status} gap={1}>
            <Box w={3} h={3} bg={`${color}.500`} borderRadius="sm" />
            <Text fontSize="xs" color="gray.600" textTransform="capitalize">
              {status.replace("_", " ")}
            </Text>
          </HStack>
        ))}
      </Flex>

      {/* Session detail overlay */}
      {selectedSession && (
        <>
          <Box
            position="fixed"
            inset={0}
            bg="blackAlpha.400"
            zIndex={99}
            onClick={() => setSelectedSession(null)}
          />
          <SessionDetail
            session={selectedSession}
            onClose={() => setSelectedSession(null)}
          />
        </>
      )}
    </Box>
  )
}

export default ClassCalendar
